import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { Badge, Button, ErrorAlert } from '@react/components';
import type { DocumentVersion } from '@core/types/document';
import { documentStatusMap, formatDate, getStatusMapping } from './logic';
import {
  buildRestoreRevisionRequest,
  diffVersionSnapshots,
  type BlockDiffKind,
  type DiffDocumentBlock,
} from './versionDiff';

export interface DocumentVersionsPanelProps {
  documentId: string;
  productId: string;
  onChanged: () => void | Promise<void>;
}

type LoadedVersion = DocumentVersion & { content_snapshot?: unknown };

const DIFF_BADGE_VARIANTS: Record<BlockDiffKind, 'default' | 'success' | 'warning' | 'error' | 'secondary'> = {
  added: 'success',
  removed: 'error',
  changed: 'warning',
  moved: 'secondary',
  unchanged: 'default',
};

function versionId(version: DocumentVersion): string {
  return version.entity?.id || (version as DocumentVersion & { id?: string }).id || '';
}

function versionName(version: DocumentVersion): string {
  return version.version_label || `v${version.version_number}`;
}

function blockTypeName(type: string): string {
  return type.replace(/^BLOCK_TYPE_/, '').toLowerCase().replace(/_/g, ' ');
}

function BlockContent({ block }: { block?: DiffDocumentBlock }) {
  const { t } = useI18n();
  if (!block) {
    return <p className="text-xs text-muted-foreground">{t('document.versions.diff.noBlock')}</p>;
  }
  const type = blockTypeName(block.type);
  return (
    <div className="min-w-0 space-y-1">
      <div className="text-[11px] text-muted-foreground">{type}</div>
      <p className="whitespace-pre-wrap break-words text-xs leading-5 text-foreground">
        {block.text || t('document.versions.diff.unsupportedBlock', { type })}
      </p>
      {block.isAttachment && (
        <p className="text-[11px] leading-4 text-muted-foreground">
          {t('document.versions.diff.attachmentNoBinary')}
        </p>
      )}
    </div>
  );
}

export const DocumentVersionsPanel = observer(({
  documentId,
  productId,
  onChanged,
}: DocumentVersionsPanelProps) => {
  const { t, locale } = useI18n();
  const store = useDocument();
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<unknown>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [comparison, setComparison] = useState<{ old: LoadedVersion | null; new: LoadedVersion | null }>({ old: null, new: null });
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState<unknown>(null);
  const [onlyChanges, setOnlyChanges] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const listRequest = useRef(0);
  const comparisonRequest = useRef(0);

  const loadVersions = useCallback(async () => {
    const request = ++listRequest.current;
    setListLoading(true);
    setListError(null);
    try {
      const response = await store.ListVersions('document', documentId);
      if (request === listRequest.current) setVersions(response?.versions?.versions ?? []);
    } catch (error) {
      if (request === listRequest.current) setListError(error);
    } finally {
      if (request === listRequest.current) setListLoading(false);
    }
  }, [documentId, store]);

  useEffect(() => {
    setSelected(new Set());
    setComparison({ old: null, new: null });
    void loadVersions();
    return () => {
      listRequest.current += 1;
      comparisonRequest.current += 1;
    };
  }, [documentId, loadVersions]);

  const comparedVersions = useMemo(() => versions
    .filter((version) => selected.has(version.version_number))
    .sort((a, b) => a.version_number - b.version_number), [selected, versions]);

  const loadComparison = useCallback(async () => {
    const request = ++comparisonRequest.current;
    setComparisonError(null);
    setComparison({ old: null, new: null });
    if (comparedVersions.length !== 2) {
      setComparisonLoading(false);
      return;
    }

    const [oldVersion, newVersion] = comparedVersions;
    const oldId = versionId(oldVersion);
    const newId = versionId(newVersion);
    if (!oldId || !newId) {
      setComparisonError(new Error(t('document.versions.diff.loadError')));
      return;
    }

    setComparisonLoading(true);
    try {
      const [oldResponse, newResponse] = await Promise.all([
        store.GetVersion('document', documentId, oldId),
        store.GetVersion('document', documentId, newId),
      ]);
      if (request !== comparisonRequest.current) return;
      setComparison({
        old: (oldResponse?.version ?? oldResponse) as LoadedVersion,
        new: (newResponse?.version ?? newResponse) as LoadedVersion,
      });
    } catch (error) {
      if (request === comparisonRequest.current) setComparisonError(error);
    } finally {
      if (request === comparisonRequest.current) setComparisonLoading(false);
    }
  }, [comparedVersions, documentId, store, t]);

  useEffect(() => {
    void loadComparison();
    return () => {
      comparisonRequest.current += 1;
    };
  }, [loadComparison]);

  const diff = useMemo(() => {
    if (!comparison.old || !comparison.new) return null;
    return diffVersionSnapshots(
      comparison.old.snapshot ?? comparison.old.content_snapshot,
      comparison.new.snapshot ?? comparison.new.content_snapshot,
    );
  }, [comparison]);

  const toggle = (versionNumber: number) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(versionNumber)) next.delete(versionNumber);
      else if (next.size < 2) next.add(versionNumber);
      return next;
    });
  };

  const restore = async (version: DocumentVersion) => {
    setBusy(version.version_number);
    setActionError(null);
    try {
      const request = buildRestoreRevisionRequest(
        documentId,
        productId,
        version,
        t('document.versions.restoredFrom', { version: versionName(version) }),
      );
      await store.CreateRevision('document', documentId, request as any);
      await onChanged();
      await loadVersions();
    } catch (error) {
      setActionError(error);
    } finally {
      setBusy(null);
    }
  };

  const visibleEntries = diff?.entries.filter((entry) => !onlyChanges || entry.kind !== 'unchanged') ?? [];

  return (
    <div className="space-y-4">
      {actionError != null && <ErrorAlert error={actionError} dismissible onDismiss={() => setActionError(null)} />}

      {listLoading ? (
        <div className="space-y-2 py-1" aria-busy="true">
          <span className="sr-only">{t('common.loading')}</span>
          {Array.from({ length: 3 }).map((_, index) => <div key={index} className="h-12 animate-pulse rounded-md bg-muted" />)}
        </div>
      ) : listError != null ? (
        <div className="space-y-2">
          <ErrorAlert error={listError} />
          <Button type="button" variant="secondary" size="sm" onClick={() => void loadVersions()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : versions.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">{t('document.versions.empty')}</p>
      ) : (
        <ul className="divide-y divide-border border-y border-border">
          {versions.map((version) => {
            const status = version.status ? getStatusMapping(documentStatusMap, version.status) : null;
            const name = versionName(version);
            return (
              <li key={versionId(version) || version.version_number} className="py-2.5">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={selected.has(version.version_number)}
                    disabled={!selected.has(version.version_number) && selected.size >= 2}
                    onChange={() => toggle(version.version_number)}
                    aria-label={t('document.versions.selectForCompare', { version: name })}
                    className="mt-1 rounded border-border"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-sm tabular-nums text-foreground">{name}</span>
                      {status && <Badge variant={status.variant} size="sm">{t(status.labelKey)}</Badge>}
                    </div>
                    {version.title && <p className="mt-0.5 break-words text-xs text-muted-foreground">{version.title}</p>}
                    <p className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
                      {version.approved_by?.display_name ? `${version.approved_by.display_name} · ` : ''}
                      {formatDate(version.approved_at || version.created_at, locale)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="shrink-0 px-0 text-xs"
                    isLoading={busy === version.version_number}
                    disabled={busy !== null}
                    onClick={() => void restore(version)}
                  >
                    {t('document.versions.restoreVersion', { version: name })}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {comparedVersions.length < 2 ? (
        <p className="text-xs text-muted-foreground">{t('document.versions.diff.hint')}</p>
      ) : (
        <section aria-label={t('document.versions.diff.title')} aria-busy={comparisonLoading} className="space-y-3 border-t border-border pt-3">
          <h3 className="text-sm font-medium text-foreground">
            {t('document.versions.comparing', { a: versionName(comparedVersions[0]), b: versionName(comparedVersions[1]) })}
          </h3>

          {comparisonLoading ? (
            <div className="space-y-2" aria-live="polite">
              <span className="text-xs text-muted-foreground">{t('common.loading')}</span>
              <div className="h-20 animate-pulse rounded-md bg-muted" />
            </div>
          ) : comparisonError != null ? (
            <div className="space-y-2">
              <ErrorAlert error={comparisonError} />
              <Button type="button" variant="secondary" size="sm" onClick={() => void loadComparison()}>
                {t('common.retry')}
              </Button>
            </div>
          ) : diff && (diff.oldSnapshot.error || diff.newSnapshot.error) ? (
            <div role="alert" className="space-y-1 text-xs text-destructive">
              {diff.oldSnapshot.error && <p>{t('document.versions.diff.invalidSnapshot', { version: versionName(comparedVersions[0]) })}</p>}
              {diff.newSnapshot.error && <p>{t('document.versions.diff.invalidSnapshot', { version: versionName(comparedVersions[1]) })}</p>}
            </div>
          ) : diff ? (
            <>
              <div className="flex flex-wrap items-center gap-1" role="group" aria-label={t('document.versions.diff.displayMode')}>
                <Button type="button" size="sm" variant={onlyChanges ? 'secondary' : 'ghost'} aria-pressed={onlyChanges} onClick={() => setOnlyChanges(true)}>
                  {t('document.versions.diff.onlyChanges')}
                </Button>
                <Button type="button" size="sm" variant={!onlyChanges ? 'secondary' : 'ghost'} aria-pressed={!onlyChanges} onClick={() => setOnlyChanges(false)}>
                  {t('document.versions.diff.allBlocks')}
                </Button>
              </div>

              <div className="flex flex-wrap gap-1.5" aria-live="polite">
                {(['added', 'removed', 'changed', 'moved'] as const).map((kind) => diff.summary[kind] > 0 && (
                  <Badge key={kind} variant={DIFF_BADGE_VARIANTS[kind]} size="sm">
                    {t(`document.versions.diff.${kind}`)} · {diff.summary[kind]}
                  </Badge>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px] font-medium text-muted-foreground" aria-hidden="true">
                <span>{t('document.versions.diff.oldVersion', { version: versionName(comparedVersions[0]) })}</span>
                <span>{t('document.versions.diff.newVersion', { version: versionName(comparedVersions[1]) })}</span>
              </div>

              {visibleEntries.length === 0 ? (
                <p className="py-4 text-center text-xs text-muted-foreground">{t('document.versions.diff.noChanges')}</p>
              ) : (
                <ul className="divide-y divide-border border-y border-border">
                  {visibleEntries.map((entry) => (
                    <li key={entry.id} className="space-y-2 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant={DIFF_BADGE_VARIANTS[entry.kind]} size="sm">
                          {t(`document.versions.diff.${entry.kind}`)}
                        </Badge>
                        {entry.kind === 'changed' && entry.moved && (
                          <Badge variant={DIFF_BADGE_VARIANTS.moved} size="sm">{t('document.versions.diff.moved')}</Badge>
                        )}
                        <code className="min-w-0 break-all text-[10px] text-muted-foreground">{entry.id}</code>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="min-w-0 border-r border-border pr-2">
                          <BlockContent block={entry.oldBlock} />
                        </div>
                        <div className="min-w-0">
                          <BlockContent block={entry.newBlock} />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : null}
        </section>
      )}
    </div>
  );
});

export default DocumentVersionsPanel;
